import { createClient } from '@supabase/supabase-js';

// Initialize Supabase client
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

export const supabase = createClient(supabaseUrl || '', supabaseKey || '');

// Types
export interface SolanaPurchase {
  id?: string;
  user_address: string;
  referrer_address?: string; 
  amount_usd: number;
  token_amount: number;
  token_type: 'SOL' | 'USDC' | 'USDT';
  is_staked: boolean;
  transaction_hash: string;
  created_at?: string;
}

export interface EthPurchase {
  id?: string;
  user_address: string;
  referrer_address?: string; 
  amount_usd: number;
  token_amount: number;
  token_type: 'ETH' | 'BNB' | 'USDC' | 'USDT';
  network: 'eth' | 'bsc';
  is_staked: boolean;
  transaction_hash: string;
  created_at?: string;
}

export interface Referral {
  id?: string;
  referrer_address: string;
  referee_address: string;
  amount: number;  // Amount purchased in USD
  reward: number;  // Reward amount in tokens
  created_at?: string;
}

// Interface for wallet connections with referrals
export interface WalletConnection {
  wallet_address: string;
  referrer_address?: string;
  wallet_type: 'evm' | 'sol';
  connected_at?: string;
}

// Functions to interact with Supabase
export const saveSolanaPurchase = async (purchase: SolanaPurchase) => {
  try {
    // Pirma bandome įrašyti pilną objektą
    const { data, error } = await supabase
      .from('solana_purchases')
      .insert(purchase)
      .select();
    
    // Jei gauname klaidą dėl "is_staked" stulpelio
    if (error && error.message?.includes('is_staked')) {
      // Sukuriame naują objektą be is_staked lauko
      const { is_staked, ...purchaseWithoutStaked } = purchase;
      
      // Bandome įrašyti be is_staked lauko
      const { data: fallbackData, error: fallbackError } = await supabase
        .from('solana_purchases')
        .insert(purchaseWithoutStaked)
        .select();
      
      if (fallbackError) {
        return null; // Grąžiname null vietoj klaidos metimo
      }
      
      return fallbackData?.[0] || null;
    }
    
    if (error) {
      return null; // Grąžiname null vietoj klaidos metimo
    }
    
    return data?.[0] || null;
  } catch (e) {
    return null; // Grąžiname null bet kokiu klaidos atveju
  }
};

export const saveReferral = async (referral: Referral) => {
  try {
    const { data, error } = await supabase
      .from('referrals')
      .insert(referral)
      .select();
    
    if (error) {
      return null;
    }
    
    return data?.[0] || null;
  } catch (e) {
    return null;
  }
};

export const getUserReferrals = async (userAddress: string) => {
  try {
    const { data, error } = await supabase
      .from('referrals')
      .select('*')
      .eq('referrer_address', userAddress);
    
    if (error) {
      return [];
    }
    
    return data || [];
  } catch (e) {
    return [];
  }
};

export const getUserReferralStats = async (userAddress: string) => {
  try {
    const { data, error } = await supabase
      .from('referrals')
      .select('amount, reward, currency')
      .eq('referrer_address', userAddress);
    
    if (error) {
      return {
        total_referrals: 0,
        total_amount: 0,
        total_rewards: 0,
        currency: 'USD'
      };
    }
    
    const referrals = data || [];
    return {
      total_referrals: referrals.length,
      total_amount: referrals.reduce((sum, ref) => sum + (ref.amount || 0), 0),
      total_rewards: referrals.reduce((sum, ref) => sum + (ref.reward || 0), 0),
      currency: referrals.length > 0 ? referrals[0].currency || 'USD' : 'USD'
    };
  } catch (e) {
    return {
      total_referrals: 0,
      total_amount: 0,
      total_rewards: 0,
      currency: 'USD'
    };
  }
};

export const getTopReferrers = async (limit = 10) => {
  try {
    const { data, error } = await supabase
      .from('top_referrers')
      .select('*')
      .limit(limit);
    
    if (error) {
      return [];
    }
    
    return data || [];
  } catch (e) {
    return [];
  }
};

// Save wallet connection with referral info
export const saveWalletConnection = async (connection: WalletConnection) => {
  try {
    // First, check if this wallet has already been recorded
    const { data: existing } = await supabase
      .from('wallet_connections')
      .select('*')
      .eq('wallet_address', connection.wallet_address)
      .maybeSingle();
      
    // If wallet already exists, don't update the referrer (first referrer gets credit)
    if (existing) {
      return existing;
    }
    
    // If it's a new wallet and there's a referrer, save the connection
    const { data, error } = await supabase
      .from('wallet_connections')
      .insert(connection)
      .select();
    
    if (error) {
      throw error;
    }
    
    return data?.[0];
  } catch (error) {
    // Don't throw, just return null to prevent interrupting the user experience
    return null;
  }
}; 